// DEV-ONLY mock helpers, gated entirely on the REACT_APP_MOCK_BLOCKED_SENSOR
// env var. When the var is unset (production, normal dev) every function here is
// a no-op pass-through, so this file has ZERO effect unless you opt in.
//
// Purpose: preview the "sensor blocked" (isCameraObstructed) dashboard changes
// without a real blocked device. The mock is injected at the data layer, so the
// signal flows through the REAL code paths — the hourly alarms_v2 currentAlarms
// record AND the live device-status camera_obstructed flag — exactly as a real
// blocked sensor would.
//
// Value semantics of REACT_APP_MOCK_BLOCKED_SENSOR:
//   unset / ""        → no mock (default)
//   "FIRST"           → treat only the FIRST sensor queried as blocked (one
//                       sensor, no name needed — good for the ranking demo)
//   "*"               → treat EVERY sensor as blocked
//   "name1,name2,…"   → treat only these exact sensor names as blocked
//
// Run (PowerShell):  $env:REACT_APP_MOCK_BLOCKED_SENSOR="FIRST"; npm start

// Type-only, so it is erased at compile time and creates no runtime import cycle
// with deviceStatus.ts (which imports this module).
import type { DeviceStatus } from "./deviceStatus";

// NODE_ENV first, and this is the load-bearing line. REACT_APP_* is a BUILD-TIME
// substitution, CRA reads .env from the repo root during the build, and this repo
// has a TRACKED .env already carrying REACT_APP_API_URL -- so the natural place to
// add REACT_APP_MOCK_BLOCKED_SENSOR is a committed file that ships. The deploy
// path would not catch it either: main.yml sets `CI: false` so lint warnings
// cannot fail the build, then syncs straight to S3 and invalidates CloudFront.
// With "*" baked in, EVERY sensor renders Critical/blocked on the live dashboard
// -- indistinguishable from a fleet-wide outage, and caused by the mock working
// exactly as designed.
//
// react-scripts build sets NODE_ENV=production and a .env file cannot override
// it, so this turns "confirm nobody sets that variable" into "it does not matter
// if they do".
const RAW =
  process.env.NODE_ENV === "production"
    ? ""
    : (process.env.REACT_APP_MOCK_BLOCKED_SENSOR || "").trim();
const NAMES = RAW.split(",").map((s) => s.trim()).filter(Boolean);
const ALL = NAMES.includes("*");
const FIRST = NAMES.length === 1 && NAMES[0].toUpperCase() === "FIRST";

let firstSeen: string | null = null;
let announced = false;
function announceOnce() {
  if (announced || !RAW) return;
  announced = true;
  // eslint-disable-next-line no-console
  console.warn(
    `[devMocks] Mocking blocked sensor(s): ` +
      `${ALL ? "ALL" : FIRST ? "FIRST queried" : NAMES.join(", ")}` +
      ` (REACT_APP_MOCK_BLOCKED_SENSOR). Unset the env var to disable.`
  );
}

/** Whether a given sensor name should be presented as blocked. */
export function isMockBlockedSensor(name: string): boolean {
  if (!RAW) return false;
  announceOnce();
  if (ALL) return true;
  if (FIRST) {
    if (firstSeen === null) {
      firstSeen = name;
      // eslint-disable-next-line no-console
      console.warn(`[devMocks] Blocked sensor = "${name}" (first queried).`);
    }
    return name === firstSeen;
  }
  return NAMES.includes(name);
}

// Timestamp for the synthetic record. new Date() is off-limits in some contexts,
// but this is browser dev code, so it's fine here. Formatted with a space to
// match the API's `datetime` shape.
/** Format as the API does: space-separated, no zone suffix. */
function mockDatetimeString(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ` +
    `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
  );
}

/**
 * A datetime guaranteed to beat every record in `records` under the consumer's
 * own comparison.
 *
 * isCurrentlyBlocked ignores ORDER entirely -- it scans every record and keeps
 * the maximum parsed timestamp -- so "prepend the newest record" was never a
 * strategy. The synthetic record only wins if its datetime PARSES larger than
 * every real one.
 *
 * It used to be built from `new Date()` local clock fields, and
 * `Date.parse("YYYY-MM-DDTHH:mm:ss")` with no zone is local. If the API's
 * datetime is UTC, a real record from ten minutes ago also parses as local and
 * therefore sits AHEAD of local-now by the viewer's UTC offset -- five hours in
 * America/New_York. The real record wins, the mock silently does nothing for
 * exactly the sensors with recent alarm activity, and the live pill still flips,
 * so half the preview works. That is the confusing failure.
 *
 * Deriving the timestamp from the records themselves is correct whichever
 * convention the server uses, which matters because I could not establish it:
 * the IoT rules emit UTC with a Z for `created_at`, but the alarms_v2 `datetime`
 * range key is written elsewhere and the firmware uses both gmtime and
 * localtime. This removes the need to know.
 *
 * Self-consistent by construction: mockDatetimeString writes local fields and
 * Date.parse reads local fields, so the value round-trips to exactly base + 1s.
 */
function mockDatetimeAfter(records: AlarmRecord[]): string {
  const parsed = records
    .map((r) => Date.parse(String(r.datetime ?? "").replace(" ", "T")))
    .filter((n) => !Number.isNaN(n));
  const base = Math.max(Date.now(), ...parsed);
  return mockDatetimeString(new Date(base + 1000));
}

/**
 * The two shape assumptions this mock's fidelity rests on, written as types
 * rather than as comments. The file's whole claim is that the signal flows
 * through the REAL code paths, so a mock that drifts from the real shape
 * produces a preview that LOOKS correct -- a worse failure than one that
 * crashes. `any` on both parameters meant TypeScript checked neither.
 *
 * What these types do NOT encode, because TypeScript cannot: an array type
 * expresses no ordering, and `datetime: string` expresses no format. I claimed
 * otherwise in review and was wrong. Both assumptions are now handled in CODE
 * instead -- mockDatetimeAfter derives its timestamp from the records rather
 * than trusting a format, and the newest record is found by comparison rather
 * than by position -- which is why neither needs to be expressed in a type.
 */
type AlarmRecord = {
  datetime?: string;
  currentAlarms?: string[];
  currAlarms?: string[];
  prevAlarms?: string[];
  __mockBlocked?: boolean;
};

type AlarmsEnvelope = { data?: AlarmRecord[] } | null | undefined;

/**
 * Prepend a synthetic newest alarms_v2 record carrying isCameraObstructed, so
 * isCurrentlyBlocked() sees the sensor as currently blocked. Pass-through when
 * the sensor isn't mocked. `envelope` is the { data: [...] } shape getSensorAlarms
 * returns.
 */
export function injectMockBlockedAlarms(
  sensorName: string,
  envelope: AlarmsEnvelope
): AlarmsEnvelope {
  if (!isMockBlockedSensor(sensorName)) return envelope;
  // Hoisted so the Array.isArray narrowing applies to the value being used;
  // `envelope.data` after `Array.isArray(envelope?.data)` is safe at runtime but
  // not provably so, which is the kind of thing the previous `any` hid.
  const data = envelope?.data;
  const records: AlarmRecord[] = Array.isArray(data) ? data : [];

  // prevAlarms is seeded from the NEWEST record, not records[0]. Those are the
  // same distinction the line below turns on: the consumer compares timestamps
  // and ignores position, so "first" and "newest" are unrelated. Only the
  // new-alert diff reads prevAlarms, so this was cosmetic -- but it read as
  // deliberate and it is free once the newest record is in hand.
  let newest: AlarmRecord | undefined;
  let newestMs = -Infinity;
  for (const rec of records) {
    const ts = Date.parse(String(rec?.datetime ?? "").replace(" ", "T"));
    if (Number.isNaN(ts) || ts <= newestMs) continue;
    newestMs = ts;
    newest = rec;
  }
  const prev = newest?.currentAlarms || newest?.currAlarms || [];

  const synthetic = {
    datetime: mockDatetimeAfter(records),
    currentAlarms: Array.from(new Set([...prev, "isCameraObstructed"])),
    prevAlarms: prev,
    __mockBlocked: true,
  };
  return { ...(envelope || {}), data: [synthetic, ...records] };
}

/**
 * Force camera_obstructed on a live device-status result so the live pill shows
 * too. When the real reply was null (no device state), returns a minimal blocked
 * result so the badge still flips. Pass-through when the sensor isn't mocked.
 */
export function injectMockBlockedDeviceStatus(
  sensorName: string,
  status: DeviceStatus | null
): DeviceStatus | null {
  if (!isMockBlockedSensor(sensorName)) return status;
  if (!status) {
    return { alarms: { camera_obstructed: true }, hottestTempC: null, stovePoweredOn: null };
  }
  return {
    ...status,
    alarms: { ...(status.alarms || {}), camera_obstructed: true },
  };
}
